<script setup>
/**
 * The workroom's chat: the internal notes and the audit trail as one
 * chronological feed with a pinned composer.
 *
 * Two lists that describe the same ticket were two columns the reader had to
 * interleave in their head; `mergeCaseFeed` does it instead, so a note and the
 * stage change it argued for sit next to each other.
 *
 * Walled off from the customer by construction — these notes have no
 * customer-facing surface at all — which is why the lock marker sits in the pane
 * header permanently rather than only above the composer. There is no
 * case-to-customer message API either, so the one way out to the customer is a
 * link into the conversation rather than a toggle that would refuse to send.
 *
 * Replies keep the server's contract (`parent_id`, one level) but are not
 * nested: a feed that reorders itself to nest is no longer chronological, so the
 * bubble quotes the name it is answering instead.
 */
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import CaseCommentComposer from './CaseCommentComposer.vue';
import CaseCommentItem from './CaseCommentItem.vue';
import {
  activityLabel,
  activityMeta,
  actorName,
  lastActivityLabel,
  mergeCaseFeed,
} from 'dashboard/helper/supportCaseMeta';

const props = defineProps({
  // The ticket this feed belongs to; a change means the pane is looking at a
  // different record and must not carry a reply target across.
  caseId: {
    type: [Number, String],
    default: null,
  },
  // Oldest first, as the API returns them.
  comments: {
    type: Array,
    default: () => [],
  },
  // Newest first, as the API returns them; the merge sorts.
  activities: {
    type: Array,
    default: () => [],
  },
  currentUserId: {
    type: [Number, String],
    default: null,
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
  isLoadingThread: {
    type: Boolean,
    default: false,
  },
  isLoadingActivities: {
    type: Boolean,
    default: false,
  },
  // The lists an owner id may resolve to a name against; an id outside them
  // keeps the generic line rather than printing a number at the reader.
  knownUsers: {
    type: Array,
    default: () => [],
  },
  knownTeams: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  primaryConversationLink: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['post', 'edit', 'archive', 'openConversation']);

const { t } = useI18n();

// The comment being replied to; `null` is a root note.
const replyTo = ref(null);

const feedRef = ref(null);

const entries = computed(() =>
  mergeCaseFeed(props.comments, props.activities, {
    currentUserId: props.currentUserId,
  })
);

// A conversation this agent cannot open is not an offer worth making.
const customerConversationLink = computed(() =>
  props.primaryConversationLink &&
  !props.primaryConversationLink.conversation?.restricted
    ? props.primaryConversationLink
    : null
);

// Only an empty feed can be "loading": once there is something to read, a
// second fetch still in flight is the pane's business, not the reader's.
const isLoadingFeed = computed(
  () =>
    (props.isLoadingThread || props.isLoadingActivities) &&
    !entries.value.length
);

// `{actor} · {label} · {time}`, assembled here rather than interleaved with
// separators in the template. An actor-less change is the system's, not
// nobody's, a rule's change is the rule's, and a row with no readable timestamp
// simply says less.
const activitySentence = activity =>
  [
    actorName(activity.actor, t),
    activityLabel(activity, t, {
      users: props.knownUsers,
      teams: props.knownTeams,
    }),
    lastActivityLabel(activity.createdAt),
  ]
    .filter(Boolean)
    .join(' · ');

// A pane this size is always read from the bottom; there is no "am I scrolled
// up" heuristic to respect until the thread pages.
const scrollToBottom = () => {
  nextTick(() => {
    if (feedRef.value) feedRef.value.scrollTop = feedRef.value.scrollHeight;
  });
};

onMounted(scrollToBottom);

watch(
  () => entries.value.length,
  (count, previousCount) => {
    if (count > previousCount) scrollToBottom();
  }
);

// A ticket replaced under the pane takes its reply target with it: the parent
// belongs to the thread that left, and posting against it would answer a
// comment this ticket never had.
watch(
  () => props.caseId,
  () => {
    replyTo.value = null;
  }
);

const onPost = content => {
  emit('post', { content, parentId: replyTo.value?.id ?? null });
  replyTo.value = null;
};
</script>

<template>
  <section
    class="flex flex-col h-full min-h-0"
    data-testid="case-chat-pane"
    :aria-label="t('SUPPORT_CASES.DETAIL.THREAD.TITLE')"
  >
    <header
      class="flex flex-wrap items-baseline gap-2 px-3 py-2.5 border-b shrink-0 border-n-weak"
    >
      <span
        class="inline-flex items-center gap-1 text-xs text-n-slate-11"
        data-testid="case-thread-internal-marker"
      >
        <Icon icon="i-lucide-lock" class="size-3 shrink-0" />
        {{ t('SUPPORT_CASES.DETAIL.THREAD.INTERNAL_NOTE') }}
      </span>
    </header>

    <ol
      v-if="entries.length"
      ref="feedRef"
      class="flex flex-col min-h-0 gap-3 px-3 py-3 overflow-y-auto grow"
      :aria-label="t('SUPPORT_CASES.DETAIL.THREAD.FEED_ARIA')"
      data-testid="case-chat-feed"
    >
      <template v-for="entry in entries" :key="entry.id">
        <li v-if="entry.kind === 'comment'" class="min-w-0">
          <CaseCommentItem
            :comment="entry.comment"
            :current-user-id="currentUserId"
            :is-admin="isAdmin"
            :is-reply="Boolean(entry.comment.parentId)"
            :align="entry.align"
            :is-grouped="entry.isGrouped"
            :reply-to-name="entry.replyToName"
            :disabled="disabled"
            @reply="replyTo = $event"
            @edit="emit('edit', $event)"
            @archive="emit('archive', $event)"
          />
        </li>
        <li
          v-else
          class="self-center inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded-full bg-n-alpha-2 text-xs text-n-slate-11"
          :data-testid="`case-activity-${entry.activity.id}`"
        >
          <Icon
            :icon="activityMeta(entry.activity.eventType).icon"
            class="size-3 shrink-0"
          />
          <span
            class="min-w-0 truncate"
            :title="activitySentence(entry.activity)"
          >
            {{ activitySentence(entry.activity) }}
          </span>
        </li>
      </template>
    </ol>

    <p
      v-else-if="isLoadingFeed"
      class="px-3 py-3 mb-0 text-sm grow text-n-slate-11"
      data-testid="case-chat-loading"
    >
      {{ t('SUPPORT_CASES.DETAIL.THREAD.LOADING') }}
    </p>

    <p
      v-else
      class="px-3 py-3 mb-0 text-sm grow text-n-slate-11"
      data-testid="case-chat-empty"
    >
      {{ t('SUPPORT_CASES.DETAIL.THREAD.EMPTY') }}
    </p>

    <div
      class="flex flex-col gap-1.5 px-3 py-2.5 border-t shrink-0 border-n-weak"
    >
      <p
        v-if="replyTo"
        class="mb-0 text-xs text-n-slate-11"
        data-testid="case-thread-replying-to"
      >
        {{
          t('SUPPORT_CASES.DETAIL.THREAD.REPLYING_TO', {
            name: replyTo.author?.availableName || replyTo.author?.name || '',
          })
        }}
      </p>

      <CaseCommentComposer
        :cancellable="Boolean(replyTo)"
        :disabled="disabled"
        submit-hint
        note-badge
        :placeholder="
          replyTo
            ? t('SUPPORT_CASES.DETAIL.THREAD.REPLY_PLACEHOLDER')
            : t('SUPPORT_CASES.DETAIL.THREAD.PLACEHOLDER')
        "
        @submit="onPost"
        @cancel="replyTo = null"
      />

      <Button
        v-if="customerConversationLink"
        variant="link"
        color="slate"
        size="xs"
        class="ltr:mr-auto rtl:ml-auto"
        :label="t('SUPPORT_CASES.DETAIL.THREAD.OPEN_CONVERSATION')"
        data-testid="case-chat-open-conversation"
        @click="emit('openConversation', customerConversationLink)"
      />
    </div>
  </section>
</template>
